/*
  Nature Lane Beauty — routine buy module, for sections/nlb-routine-buy.liquid.

  Three jobs, all of them enhancement:

    1. <nlb-routine-form> — Ajax add-to-cart that opens the drawer.
    2. Custom build #8's purchase-option selector, scoped to the section.
    3. Custom build #2's sticky mobile bar, revealed once the buy box scrolls
       off the top.

  ── Why this is not Dawn's <product-form> ────────────────────────────────────

  ProductForm's constructor runs `this.variantIdInput.disabled = false`, where
  `variantIdInput` is `form.querySelector('[name=id]')`. A routine sold as three
  line items posts `items[0][id]`, `items[1][id]`, `items[2][id]` and no
  `[name=id]` at all, so that line throws a TypeError before the element has
  bound anything and the form falls back to a native POST — which works, but
  reloads the page and never opens the drawer.

  Rather than run two code paths, this element handles both shapes. The request
  is the same either way: FormData straight off the form, plus the drawer's
  section ids, posted to routes.cart_add_url.

  ── The one thing that is not enhancement ────────────────────────────────────

  A one-time purchase must post no `selling_plan` field at all, because the cart
  rejects an empty one. Only JS can arrange that, so the fieldset ships hidden
  and is revealed here. With the script off, the section degrades to a working
  one-time purchase rather than to a subscribe option that silently charges the
  one-time price.

  Prices come from data attributes the section wrote with Shopify's `money`
  filter. Nothing here does arithmetic on money.
*/
(function () {
  'use strict';

  /* ------------------------------------------------------------------ cart */

  if (!customElements.get('nlb-routine-form')) {
    customElements.define(
      'nlb-routine-form',
      class NlbRoutineForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form || this.form.dataset.nlbRoutineBound === 'true') return;
          this.form.dataset.nlbRoutineBound = 'true';

          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.buttons = this.querySelectorAll('[type="submit"]');
          this.spinner = this.querySelector('.loading__spinner');
          this.errorWrapper = this.querySelector('.nlb-routine-buy__error');
          this.errorText = this.querySelector('[data-nlb-routine-error]');

          if (document.querySelector('cart-drawer')) {
            this.buttons.forEach(function (button) {
              button.setAttribute('aria-haspopup', 'dialog');
            });
          }

          this.form.addEventListener('submit', this.onSubmit.bind(this));
        }

        onSubmit(event) {
          // Without the cart JS on the page there is nothing to render into, so
          // the native POST is left to do its job and reload to /cart.
          if (typeof fetchConfig !== 'function' || !window.routes || !window.routes.cart_add_url) return;

          event.preventDefault();
          if (this.busy) return;
          this.setBusy(true);
          this.showError('');

          var config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          var formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map(function (section) {
                return section.id;
              })
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          var self = this;
          fetch(window.routes.cart_add_url, config)
            .then(function (response) {
              return response.json();
            })
            .then(function (response) {
              // A rejected add answers with `status` set; a good one never does.
              if (response.status) {
                self.showError(response.description || response.message || '');
                return;
              }

              if (!self.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nlb-routine-form', cartData: response });
              }

              self.cart.renderContents(response);
            })
            .catch(function (error) {
              console.error(error);
              self.showError('');
            })
            .finally(function () {
              self.setBusy(false);
              if (self.cart && self.cart.classList.contains('is-empty')) self.cart.classList.remove('is-empty');
            });
        }

        setBusy(busy) {
          this.busy = busy;
          this.buttons.forEach(function (button) {
            button.classList.toggle('loading', busy);
            if (busy) {
              button.setAttribute('aria-disabled', 'true');
            } else {
              button.removeAttribute('aria-disabled');
            }
          });
          if (this.spinner) this.spinner.classList.toggle('hidden', !busy);
        }

        showError(message) {
          if (!this.errorWrapper || !this.errorText) return;
          this.errorText.textContent = message;
          this.errorWrapper.toggleAttribute('hidden', !message);
        }
      }
    );
  }

  /* ------------------------------------------------- purchase options + bar */

  function initSection(root) {
    initPurchaseOptions(root);
    initSticky(root);
  }

  function initPurchaseOptions(root) {
    var form = root.querySelector('form');
    if (!form) return;

    var fieldset = form.querySelector('[data-nlb-routine-purchase]');
    var radios = form.querySelectorAll('[data-nlb-routine-plan]');
    if (!fieldset || radios.length === 0) return;

    // Bound once. The theme editor re-runs init on a section it re-renders,
    // which would otherwise stack a second set of listeners.
    if (fieldset.dataset.nlbRoutineBound === 'true') return;
    fieldset.dataset.nlbRoutineBound = 'true';

    var planInput = form.querySelector('[data-nlb-routine-plan-input]');
    var subscribeRadio = form.querySelector('[data-nlb-routine-plan-subscribe]');
    var intervalWrap = form.querySelector('[data-nlb-routine-interval-wrap]');
    var interval = form.querySelector('[data-nlb-routine-interval]');
    var subscribePrice = form.querySelector('[data-nlb-routine-subscribe-price]');
    var priceTargets = root.querySelectorAll('[data-nlb-routine-price]');
    var savingTarget = root.querySelector('[data-nlb-routine-saving]');

    function selected() {
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) return radios[i];
      }
      return null;
    }

    /*
      A one-time purchase means no `selling_plan` field at all. Posting it empty
      is rejected by the cart, so the input is disabled rather than blanked —
      disabled fields are not submitted.
    */
    function syncPlanInput() {
      if (!planInput) return;
      var choice = selected();
      var planId = choice ? choice.value : '';

      planInput.value = planId;
      planInput.disabled = planId === '';
    }

    function syncPrice() {
      var choice = selected();
      if (!choice || !choice.dataset.nlbRoutinePlanPrice) return;

      priceTargets.forEach(function (target) {
        target.textContent = choice.dataset.nlbRoutinePlanPrice;
      });
    }

    /*
      The saving moves with the price. Selecting a plan rewrites the headline
      price, and a saving computed against the other option would then be a
      figure contradicting the number directly above it.

      Both sentences were built by Liquid and ride on the radios — nothing here
      does arithmetic on money. An empty sentence hides the line rather than
      leaving an empty paragraph holding space, which is what happens on the
      subscribe side when the delivery plans do not all cost the same.
    */
    function syncSaving() {
      if (!savingTarget) return;
      var choice = selected();
      var text = choice ? choice.dataset.nlbRoutineSavingText || '' : '';

      savingTarget.textContent = text;
      savingTarget.hidden = text === '';
    }

    // State-driven rather than revealed once: a frequency menu under an
    // unselected subscribe radio describes a purchase nobody is making.
    function syncInterval() {
      if (!intervalWrap) return;
      intervalWrap.hidden = !(subscribeRadio && subscribeRadio.checked);
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        syncInterval();
        syncPlanInput();
        syncPrice();
        syncSaving();
      });
    });

    /*
      The select carries no name. It writes the chosen plan id into the
      subscribe radio, which the hidden input then mirrors — so there is exactly
      one selling_plan value on the form and no chance of two controls
      disagreeing about it.
    */
    if (interval && subscribeRadio) {
      interval.addEventListener('change', function () {
        var option = interval.options[interval.selectedIndex];

        subscribeRadio.value = interval.value;
        if (option && option.dataset.nlbPlanPrice) {
          subscribeRadio.dataset.nlbRoutinePlanPrice = option.dataset.nlbPlanPrice;
          if (subscribePrice) subscribePrice.textContent = option.dataset.nlbPlanPrice;
        }

        subscribeRadio.checked = true;
        syncInterval();
        syncPlanInput();
        syncPrice();
        syncSaving();
      });
    }

    // The choice only works with this script running, so it is only offered
    // with this script running.
    fieldset.removeAttribute('hidden');

    // Opening state. Usually the one-time radio the section rendered as checked,
    // but a back-navigation can restore a different one before this runs.
    syncInterval();
    syncPlanInput();
    syncPrice();
    syncSaving();
  }

  /*
    Custom build #2. The bar is a submit button inside the same form, so it
    carries no purchase state of its own — this only decides when it is on
    screen.

    Two conditions, not one:

      · the bar's own buy box has been scrolled past. Hence the
        boundingClientRect test rather than plain `isIntersecting`: not yet
        reached and already gone both read as "not intersecting", and only one
        of them should raise a bar.

      · no buy box anywhere on the page is currently visible. The routine page
        closes on a second copy of this section, and a fixed bar offering Add
        to Cart on top of a full-size Add to Cart is a button covering itself.
        So every [data-nlb-routine-watch] in the document is observed, not just
        this section's, and the bar stands down whenever any of them is on
        screen.
  */
  var stickyBars = [];
  var stickyVisible = new Set();
  var stickyObserver = null;

  function syncStickyBars() {
    var anyOnScreen = stickyVisible.size > 0;
    stickyBars.forEach(function (entry) {
      entry.bar.hidden = anyOnScreen || !entry.past;
    });
  }

  function initSticky(root) {
    var bar = root.querySelector('[data-nlb-routine-sticky]');
    var watch = root.querySelector('[data-nlb-routine-watch]');
    if (!watch) return;
    if (!('IntersectionObserver' in window)) return;

    if (!stickyObserver) {
      stickyObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              stickyVisible.add(entry.target);
            } else {
              stickyVisible.delete(entry.target);
            }

            // Only the bar that owns this buy box tracks "scrolled past".
            stickyBars.forEach(function (item) {
              if (item.watch !== entry.target) return;
              item.past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            });
          });
          syncStickyBars();
        },
        { threshold: 0 }
      );
    }

    // Every buy box is observed for the page-wide test, whether or not it has
    // a bar of its own.
    if (watch.dataset.nlbRoutineObserved !== 'true') {
      watch.dataset.nlbRoutineObserved = 'true';
      stickyObserver.observe(watch);
    }

    if (!bar || bar.dataset.nlbRoutineBound === 'true') return;
    bar.dataset.nlbRoutineBound = 'true';
    stickyBars.push({ bar: bar, watch: watch, past: false });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-nlb-routine-buy]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // The theme editor re-renders one section in place. Scoping to the event
  // target is what keeps the others from rebinding.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
